/**
 * TaskDialogContext — open the task detail/edit dialog from ANY page
 * without navigating to /requests. Iter83 rebuilt to match the reel
 * dialog template (Title / Department / Editor / Priority / Deadline /
 * Status + SCRIPT / NOTES + Files + Save / Start Work / Submit / Delete).
 *
 * Usage:
 *   const { openTaskById } = useTaskDialog();
 *   <button onClick={() => openTaskById(t.id)}>…</button>
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { uploadChunked } from "@/lib/uploadChunked";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, FileText, ExternalLink, Send, Save, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import MobileTaskChat from "@/components/MobileTaskChat";
import { useIsMobileViewport } from "@/lib/useIsMobileViewport";

const TaskDialogContext = createContext({ openTaskById: () => {} });
export const useTaskDialog = () => useContext(TaskDialogContext);

const TASK_STATUSES = ["pending", "in_progress", "submitted", "approved",
                        "disapproved", "completed", "overdue", "archived"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

// Iter153 — the task dialog is desktop-optimised (fixed columns, hover
// interactions). On phones we swap in MobileTaskChat which renders the
// same data as a WhatsApp-style chronological timeline.
export function TaskDialogProvider({ children }) {
  const [openId, setOpenId] = useState(null);
  const openTaskById = useCallback((id) => setOpenId(id || null), []);
  const value = useMemo(() => ({ openTaskById }), [openTaskById]);
  const isMobile = useIsMobileViewport();
  return (
    <TaskDialogContext.Provider value={value}>
      {children}
      {isMobile && openId ? (
        <MobileTaskChat taskId={openId} onClose={() => setOpenId(null)} />
      ) : (
        <TaskDetailDialog
          taskId={openId}
          onClose={() => setOpenId(null)} />
      )}
    </TaskDialogContext.Provider>
  );
}

function statusTone(s) {
  return ({
    pending: "bg-slate-100 text-slate-700 border-slate-300",
    in_progress: "bg-sky-100 text-sky-700 border-sky-300",
    submitted: "bg-amber-100 text-amber-700 border-amber-300",
    approved: "bg-emerald-100 text-emerald-700 border-emerald-300",
    disapproved: "bg-rose-100 text-rose-700 border-rose-300",
    completed: "bg-emerald-100 text-emerald-700 border-emerald-300",
    overdue: "bg-rose-100 text-rose-700 border-rose-300",
    archived: "bg-muted text-muted-foreground border-border",
  })[s] || "bg-muted text-muted-foreground border-border";
}

function TaskDetailDialog({ taskId, onClose }) {
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [files, setFiles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [form, setForm] = useState({
    title: "", status: "pending", priority: "medium",
    deadline: "", description: "", notes: "", assigned_to: "",
  });

  const isAdminish = ["super_admin", "admin", "manager"].includes(user?.role);

  // Fetch task + files + (admin only) employee list to populate the
  // assignee picker. Cancelled flag avoids state updates after unmount.
  useEffect(() => {
    if (!taskId) { setTask(null); setFiles([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const reqs = [api.get(`/tasks/${taskId}`)];
        if (isAdminish && employees.length === 0) {
          reqs.push(api.get("/employees").catch(() => ({ data: [] })));
        }
        const [{ data: t }, empRes] = await Promise.all(reqs);
        if (cancelled) return;
        setTask(t);
        setForm({
          title: t.title || "",
          status: t.status || "pending",
          priority: t.priority || "medium",
          deadline: (t.deadline || "").slice(0, 10),
          description: t.description || "",
          notes: t.notes || "",
          assigned_to: t.assigned_to || (t.assigned_to_ids || [])[0] || "",
        });
        if (empRes?.data) setEmployees(empRes.data);
        if ((t.files || []).length) {
          const { data: fs } = await api.get(`/files`, { params: { client_id: t.client_id } });
          if (!cancelled) setFiles((fs || []).filter((f) => t.files.includes(f.id)));
        } else {
          setFiles([]);
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e?.response?.data?.detail, e) || "Failed to load task");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const refresh = useCallback(async () => {
    if (!taskId) return;
    const { data: t } = await api.get(`/tasks/${taskId}`);
    setTask(t);
    setForm({
      title: t.title || "",
      status: t.status || "pending",
      priority: t.priority || "medium",
      deadline: (t.deadline || "").slice(0, 10),
      description: t.description || "",
      notes: t.notes || "",
      assigned_to: t.assigned_to || (t.assigned_to_ids || [])[0] || "",
    });
    if ((t.files || []).length) {
      const { data: fs } = await api.get(`/files`, { params: { client_id: t.client_id } });
      setFiles((fs || []).filter((f) => t.files.includes(f.id)));
    } else {
      setFiles([]);
    }
  }, [taskId]);

  const save = async () => {
    if (!task) return;
    setSaving(true);
    try {
      // Operator roles use the assignee carve-out: now (iter98) they can
      // also rename the task. Admin/manager keep full edit access.
      const isAssignee = task.assigned_to === user?.id
                        || (task.assigned_to_ids || []).includes(user?.id);
      const payload = (isAdminish || (!isAssignee))
        ? {
            title: form.title,
            status: form.status,
            priority: form.priority,
            deadline: form.deadline || null,
            description: form.description,
            notes: form.notes,
            assigned_to: form.assigned_to || null,
          }
        : { title: form.title, status: form.status, description: form.description, notes: form.notes };
      await api.put(`/tasks/${task.id}`, payload);
      toast.success("Saved");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const startWork = async () => {
    if (!task) return;
    try {
      await api.put(`/tasks/${task.id}`, { status: "in_progress" });
      toast.success("Work started");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, e) || "Failed");
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !task) return;
    setUploading(true);
    setUploadPct(0);
    try {
      await uploadChunked(f, {
        client_id: task.client_id,
        task_id: task.id,
        onProgress: ({ pct }) => setUploadPct(pct),
      });
      toast.success(`Uploaded ${f.name}`);
      e.target.value = "";
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, err) || "Upload failed");
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const submit = async () => {
    if (!task) return;
    try {
      await api.post(`/tasks/${task.id}/submit`);
      toast.success("Submitted for approval");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, e) || "Submit failed");
    }
  };

  const remove = async () => {
    if (!task) return;
    if (!window.confirm(`Delete task "${task.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task deleted");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, e) || "Delete failed");
    }
  };

  const open = !!taskId;
  const canEditTopFields = isAdminish;
  // Iter98 — assignees can rename their own task even if they don't have
  // admin/manager. Wider top-field edits stay admin-only.
  const isAssignee = (task?.assigned_to === user?.id)
                    || (task?.assigned_to_ids || []).includes(user?.id);
  const canEditTitle = canEditTopFields || isAssignee;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={16} className="text-teal" />
            {task?.title || (loading ? "Loading…" : "Task")}
          </DialogTitle>
        </DialogHeader>
        {loading || !task ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            <Loader2 className="inline animate-spin mr-2" size={14} />Loading…
          </div>
        ) : (
          <div className="space-y-5">
            {/* Top form grid — mirrors the reel-dialog template */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Task title</Label>
                <Input
                  value={form.title}
                  disabled={!canEditTitle}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="task-dialog-title" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Client</Label>
                <div className="px-3 py-2 rounded border border-border bg-muted/30 text-sm">{task.client_name || "—"}</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Department</Label>
                <div className="px-3 py-2 rounded border border-border bg-muted/30 text-sm capitalize">
                  {(task.department || "").replace(/_/g, " ") || "—"}
                </div>
              </div>
              {canEditTopFields && employees.length > 0 ? (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Assignee</Label>
                  <Select value={form.assigned_to || "_none"}
                          onValueChange={(v) => setForm({ ...form, assigned_to: v === "_none" ? "" : v })}>
                    <SelectTrigger data-testid="task-dialog-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Unassigned</SelectItem>
                      {employees.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Assignee</Label>
                  <div className="px-3 py-2 rounded border border-border bg-muted/30 text-sm">
                    {employees.find(e => e.id === form.assigned_to)?.name || "—"}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Priority</Label>
                <Select value={form.priority}
                        onValueChange={(v) => setForm({ ...form, priority: v })}
                        disabled={!canEditTopFields}>
                  <SelectTrigger data-testid="task-dialog-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Deadline</Label>
                <Input type="date" value={form.deadline}
                       disabled={!canEditTopFields}
                       onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                       data-testid="task-dialog-deadline" />
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Status</Label>
                <span className={`text-[10px] uppercase tracking-wider rounded px-2 py-0.5 border capitalize ${statusTone(form.status)}`}>
                  {form.status.replace(/_/g, " ")}
                </span>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="task-dialog-status" className="ml-auto w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Long-form fields — matches SCRIPT / NOTES on the reel dialog */}
            {task.source_plan_id && (
              <div className="rounded border border-purple-200 dark:border-purple-900 bg-purple-50/40 dark:bg-purple-950/20 p-3 flex items-start gap-3"
                   data-testid="task-dialog-plan-link">
                <FileText size={16} className="text-purple-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Linked content plan</div>
                  <div className="text-sm">This task was auto-created from a content plan. Open the plan brief or download the PDF for reference.</div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button asChild variant="outline" size="sm">
                    <a href={`/content-creator?plan=${task.source_plan_id}`} target="_blank" rel="noreferrer"
                       data-testid="task-dialog-plan-open">
                      Open
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <a href={`${API}/content-plans/${task.source_plan_id}/pdf`} target="_blank" rel="noreferrer"
                       data-testid="task-dialog-plan-pdf">
                      Plan PDF
                    </a>
                  </Button>
                </div>
              </div>
            )}

            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Brief / description</Label>
              <Textarea
                rows={5}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What needs to be done…"
                data-testid="task-dialog-description" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Internal notes, links, references…"
                data-testid="task-dialog-notes" />
            </div>

            {/* Files block — mirrors the reel dialog's EDITED REEL section */}
            <div className="space-y-2 pt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Files ({files.length})</Label>
                <label className="inline-flex items-center">
                  <input type="file" onChange={upload} className="hidden" data-testid="task-dialog-upload-input" />
                  <span className={`cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-border hover:bg-muted ${uploading ? "opacity-50" : ""}`}>
                    <Upload size={12} /> {uploading ? `Uploading… ${uploadPct}%` : "Upload file"}
                  </span>
                </label>
              </div>
              {uploading && (
                <div data-testid="task-dialog-upload-progress">
                  <div className="h-1.5 rounded bg-muted overflow-hidden">
                    <div
                      className="h-full bg-teal transition-all duration-200"
                      style={{ width: `${uploadPct}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Uploading… {uploadPct}%
                  </div>
                </div>
              )}
              {files.length === 0 ? (
                <div className="text-xs text-muted-foreground py-3 text-center border border-dashed rounded">
                  No files attached yet. Drag a file or click Upload.
                </div>
              ) : (
                <div className="space-y-1">
                  {files.map((f) => (
                    <a key={f.id}
                        href={`${API}/files/${f.id}/download`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-muted/40 border border-border"
                        data-testid={`task-dialog-file-${f.id}`}>
                      <FileText size={13} className="text-muted-foreground" />
                      <span className="flex-1 truncate">{f.original_filename}</span>
                      <Badge variant="outline" className="capitalize text-[9px]">
                        {(f.approval_status || "pending").replace(/_/g, " ")}
                      </Badge>
                      <ExternalLink size={11} className="text-muted-foreground" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {task && (
          <DialogFooter className="gap-2 flex-wrap pt-3 border-t border-border">
            <Button variant="outline" onClick={save} disabled={saving} data-testid="task-dialog-save">
              {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : <Save className="mr-1" size={14} />}
              Save details
            </Button>
            {form.status === "pending" && (
              <Button variant="outline" onClick={startWork}
                      className="border-teal/40 text-teal hover:bg-teal/10 hover:text-teal"
                      data-testid="task-dialog-start">
                <Play size={14} className="mr-1" /> Start Work
              </Button>
            )}
            {form.status !== "submitted" && form.status !== "approved" && (
              <Button variant="outline" onClick={submit} data-testid="task-dialog-submit">
                <Send className="mr-1" size={14} /> Submit for Approval
              </Button>
            )}
            {isAdminish && (
              <Button variant="ghost" onClick={remove}
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-auto"
                      data-testid="task-dialog-delete">
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
